import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

// Material UI components
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Services
import { updateSupplierPart, getSupplierParts } from '../../services/supplierService';

// Components
import UpcCodeManager from './UpcCodeManager';

// Validation schema
const validationSchema = Yup.object({
  supplier_part_number: Yup.string()
    .required('Supplier part number is required'),
  cost: Yup.number()
    .required('Cost is required')
    .positive('Cost must be positive')
    .typeError('Cost must be a number'),
  lead_time_days: Yup.number()
    .nullable()
    .integer('Lead time must be a whole number')
    .min(0, 'Lead time cannot be negative')
    .typeError('Lead time must be a number'),
  url: Yup.string()
    .nullable()
    .url('Please enter a valid URL')
});

const SupplierPartEditDialog = ({ open, onClose, onSuccess, supplierId, supplierPart }) => {
  const [error, setError] = useState(null);
  const [currentSupplierPart, setCurrentSupplierPart] = useState(supplierPart);

  // Refresh supplier part data to get latest UPC codes
  const refreshSupplierPart = async () => {
    try {
      const supplierParts = await getSupplierParts(supplierId);
      const updatedPart = supplierParts.find(part => part.id === supplierPart.id);
      if (updatedPart) {
        setCurrentSupplierPart(updatedPart);
      }
    } catch (err) {
      console.error('Error refreshing supplier part data:', err);
    }
  };

  // Refresh data when dialog opens
  useEffect(() => {
    if (open) {
      refreshSupplierPart();
    }
  }, [open, supplierId, supplierPart.id]);

  const initialValues = {
    supplier_part_number: supplierPart.supplier_part_number || '',
    cost: supplierPart.cost || '',
    lead_time_days: supplierPart.lead_time_days || '',
    url: supplierPart.url || ''
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);

      // Clean up values
      const submitData = {
        ...values,
        cost: parseFloat(values.cost),
        lead_time_days: values.lead_time_days ? parseInt(values.lead_time_days) : null
      };

      // Update supplier part
      await updateSupplierPart(supplierId, supplierPart.part_id, submitData);

      setSubmitting(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error updating supplier part:', err);
      setError(err.message || 'Failed to update supplier part');
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Supplier Part</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting
        }) => (
          <Form>
            <DialogContent>
              {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Part Information
                </Typography>
                <Typography variant="body1">
                  {supplierPart.part?.internal_part_number} - {supplierPart.part?.description}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="supplier_part_number"
                    label="Supplier Part Number"
                    value={values.supplier_part_number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.supplier_part_number && Boolean(errors.supplier_part_number)}
                    helperText={touched.supplier_part_number && errors.supplier_part_number}
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="cost"
                    label="Cost"
                    type="number"
                    value={values.cost}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.cost && Boolean(errors.cost)}
                    helperText={touched.cost && errors.cost}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: <span>$</span>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="lead_time_days"
                    label="Lead Time (Days)"
                    type="number"
                    value={values.lead_time_days}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lead_time_days && Boolean(errors.lead_time_days)}
                    helperText={touched.lead_time_days && errors.lead_time_days}
                    disabled={isSubmitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="url"
                    label="Part URL (Optional)"
                    type="url"
                    value={values.url}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.url && Boolean(errors.url)}
                    helperText={touched.url && errors.url}
                    disabled={isSubmitting}
                    placeholder="https://supplier-website.com/part/123"
                    InputProps={{
                      endAdornment: values.url ? (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => window.open(values.url, '_blank')}
                            disabled={!values.url || (touched.url && Boolean(errors.url))}
                            size="small"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* UPC Code Manager */}
              <UpcCodeManager
                supplierId={supplierId}
                partId={supplierPart.part_id}
                upcs={currentSupplierPart.upcs || []}
                onUpcChange={refreshSupplierPart}
              />
            </DialogContent>

            <DialogActions>
              <Button
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SupplierPartEditDialog;
